import { useState, useRef, useEffect } from "react";
import { getNounsPool } from "../EnglishTool/pool/nounsPool";
import { getVerbsPool } from "../EnglishTool/pool/verbsPool";
import { getAdjectivesPool } from "../EnglishTool/pool/adjectivesPool";
import { getAdverbsPool } from "../EnglishTool/pool/adverbsPool";
import SpeakButton from "../EnglishTool/SpeakButton";
import { awardToken } from "../../awardtoken"; 
import { db } from "../../firebase";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { useAuth } from "../../components/useAuth";

const TOOL_TYPE = "EnglishSpellingPractice";

async function logSpellingPractice({ user, type, score, total, startTime, endTime }) {
  if (!user?.uid) {
    console.warn("No user UID! User:", user);
    return;
  }
  if (total === 0) {
    console.warn("No attempts, nothing to log.");
    return;
  }
  try {
    await setDoc(
      doc(db, "users", user.uid, "practiceRecords", TOOL_TYPE),
      {
        toolType: TOOL_TYPE,
        topic: `${type} spelling`,
        numQuestions: total,
        correct: score,
        attempts: total,
        timestamp: serverTimestamp(),
        startedAt: startTime,
        finishedAt: endTime,
        durationSec: Math.round((endTime - startTime) / 1000),
      },
      { merge: true }
    );
    console.log("Spelling session log successfully written to Firestore.");
  } catch (e) {
    console.error("Error writing spelling session log:", e);
  }
}


function wrapWordArray(arr) {
  return (arr || []).map(w => (typeof w === "string" ? { word: w } : w));
}

const grade = 3; // Can be dynamic later

const POOLS = {
  noun: getNounsPool(grade),
  verb: wrapWordArray(getVerbsPool(grade)),
  adjective: wrapWordArray(getAdjectivesPool(grade)),
  adverb: wrapWordArray(getAdverbsPool(grade)),
};
const TYPES = ["noun", "verb", "adjective", "adverb"];

function randomWord(type) {
  const pool = POOLS[type];
  if (!pool || pool.length === 0) return { word: "NoWord", meaning: "" };
  return pool[Math.floor(Math.random() * pool.length)];
}

export default function EnglishSpellingPractice({ onAwardTokens }) {
  const [type, setType] = useState("noun");
  const [question, setQuestion] = useState(() => randomWord("noun"));
  const [input, setInput] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [score, setScore] = useState(0);
  const [total, setTotal] = useState(0);
  const tokensAwarded = useRef(0);
  const { user } = useAuth();
  const [sessionStart, setSessionStart] = useState(Date.now());
  const sessionStats = useRef({ score: 0, total: 0 });
  const didMountRef = useRef(false);

  useEffect(() => {
  if (!didMountRef.current) {
    didMountRef.current = true;
    setQuestion(randomWord(type));
    return;
  }
  const endTime = Date.now();
  if (sessionStats.current.total > 0) {
    logSpellingPractice({
      user,
      type,
      score: sessionStats.current.score,
      total: sessionStats.current.total,
      startTime: sessionStart,
      endTime,
    });
  }
  setSessionStart(Date.now());
  setScore(0);
  setTotal(0);
  sessionStats.current = { score: 0, total: 0 };
  setQuestion(randomWord(type));
  setFeedback(null);
  setInput("");
  // eslint-disable-next-line
}, [type]);

useEffect(() => {
  return () => {
    const endTime = Date.now();
    if (sessionStats.current.total > 0) {
      logSpellingPractice({
        user,
        type,
        score: sessionStats.current.score,
        total: sessionStats.current.total,
        startTime: sessionStart,
        endTime,
      });
    }
  };
  // eslint-disable-next-line
}, []);


  const nextQuestion = () => {
    setQuestion(randomWord(type));
    setInput("");
    setFeedback(null);
  };

const check = () => {
  const isCorrect = input.trim().toLowerCase() === question.word.toLowerCase();
  const nextTotal = total + 1;
  const nextScore = isCorrect ? score + 1 : score;
  setTotal(nextTotal);
  setScore(nextScore);
  sessionStats.current = { score: nextScore, total: nextTotal };

  // Log every answer to Firestore (session-logging)
  const endTime = Date.now();
  logSpellingPractice({
    user,
    type,
    score: nextScore,
    total: nextTotal,
    startTime: sessionStart,
    endTime,
  });

  if (isCorrect) {
    setFeedback("✅ Correct!");
    if (nextScore >= (tokensAwarded.current + 1) * 10) {
      tokensAwarded.current += 1;
      awardToken();
      if (onAwardTokens) onAwardTokens(1);
    }
    setTimeout(nextQuestion, 1000);
  } else {
    setFeedback(`❌ Incorrect. The correct spelling is: ${question.word}`);
  }
};


  return (
    <div className="english-practice-card">
      <h2>Spelling Practice</h2>
      <div>
        <label>Type: </label>
        <select value={type} onChange={e => {
          setType(e.target.value);
          setQuestion(randomWord(e.target.value));
          setFeedback(null);
          setInput("");
        }}>
          {TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
        </select>
      </div>
      <div style={{ margin: "1em 0" }}>
        <b>Spell this word:</b>
        <SpeakButton text={question.word} />
        <span style={{ marginLeft: 10, color: "#888" }}>{question.meaning || ""}</span>
      </div>
      <input
        value={input}
        onChange={e => setInput(e.target.value)}
        placeholder="Type the spelling here"
        onKeyDown={e => { if (e.key === "Enter") check(); }}
      />
      <button onClick={check}>Check</button>
      {feedback && <div style={{ marginTop: 10 }}>{feedback}</div>}
      <div style={{ marginTop: 12, fontSize: "1em" }}>
        Score: {score} / {total}
      </div>
      <button
  className="math-pill-btn"
  style={{ margin: "1em 0" }}
  onClick={() => {
    const endTime = Date.now();
    if (sessionStats.current.total > 0) {
      logSpellingPractice({
        user,
        type,
        score: sessionStats.current.score,
        total: sessionStats.current.total,
        startTime: sessionStart,
        endTime,
      });
      setSessionStart(Date.now());
      setScore(0);
      setTotal(0);
      sessionStats.current = { score: 0, total: 0 };
    }
  }}
>
  Finish Session
</button>

    </div>
  );
}
